import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { type TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { SharedContextProps } from "@lib/types";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  apiGetXAICallModels,
  apiCreateXAICallModel,
  apiUpdateXAICallModel,
  apiDeleteXAICallModel,
} from "@lib/api/xAI";
import { XAICallModel } from "@lib/types/xAI";
import { XAICallModelDrawer } from "@lib/components/PageComponents/XAICallModel";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const SharedTftSalesCallModels: React.FC<SharedContextProps> = ({
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight = 0,
  tableProps,
  height,
  queryParams,
}) => {
  const navigate = useNavigate();
  const { messages } = useIntl();
  const [selected, setSelected] = useState<XAICallModel | null>(null);
  const [openModal, setModalOpen] = useState(false);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: XAICallModel) => {
    setSelected(item);
    toggle(true);
  };

  const handleCreate = () => {
    setSelected(null);
    toggle(true);
  };

  const initialFilters: string[] = [];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: ["createdAt,DESC", "updatedAt,DESC"],
    filter: initialFilters,
    ...queryParams,
  });

  const queryKey = ["xai-call-models", JSON.stringify(params)];

  const modelsQuery = useQuery({
    queryKey,
    queryFn: () => apiGetXAICallModels(params),
    enabled: !!params && !!pageIsActive,
  });

  const { data, isLoading, isFetching, refetch } = modelsQuery;
  const apiData = data?.data?.data;

  const createMutation = useMutation({
    mutationFn: apiCreateXAICallModel,
    onSuccess() {
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["sidebar.xaiCallModels"] as string,
        ),
      );
      refetch();
      // toggle(false);
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const updateMutation = useMutation({
    mutationFn: apiUpdateXAICallModel,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["sidebar.xaiCallModels"] as string,
        ),
      );
      refetch();
      // toggle(false);
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeleteXAICallModel,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["sidebar.xaiCallModels"] as string,
        ),
      );
      refetch();
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const handleDelete = (item: XAICallModel) => {
    deleteMutation.mutate(item.id);
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<XAICallModel>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        navigate,
        apiData,
        params,
        messages,
        columnsConfig: tableProps?.columnsConfig,
        handleSelect,
        handleDelete,
      }),
    [
      apiData,
      params,
      tableProps?.columnsConfig,
      messages,
      handleSelect,
      handleDelete,
      navigate,
    ],
  );

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter.map((f) => ({
      key: f.split("||")[0],
      title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
      operator: f.split("||")[1],
      value: f.split("||")[2],
    })) as TableHeaderFilter[];
  }, [params?.filter, columns]);

  const title = customTitle || (messages["sidebar.xaiCallModels"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      minusHeight={minusHeight}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={modelsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={{ onChange, columns, ...tableProps }}
          plusProps={{
            loading: isLoading,
            disabled: isLoading,
            onClick: handleCreate,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          {...tableProps}
        />
      </AppsContent>

      <XAICallModelDrawer
        open={openModal}
        onClose={() => toggle(false)}
        item={selected}
        createMutation={createMutation}
        updateMutation={updateMutation}
      />
    </AppsContainer>
  );
};

export default SharedTftSalesCallModels;
